import { SNSEvent, SNSEventRecord, SNSMessage, SNSMessageAttribute } from '../aws-lambda-proxy'
import { MessageAttributeValue, PublishInput } from '@aws-sdk/client-sns'
import { MapLike } from '../../core'

const debug = false

export enum IOTV_Alert_Trigger_EventType {
  ALERT = 'ALERT',
  OPEN_ALERT = 'OPEN_ALERT',
  UPDATE_ALERT = 'UPDATE_ALERT',
  CANCEL_ALERT = 'CANCEL_ALERT',
}

export type IOTV_AlertTriggerSNSMessageAttributes = {
  eventName: IOTV_Alert_Trigger_EventType
}

export enum IOTV_Notifiable_EventType {
  INCIDENT_OPENED = 'INCIDENT_OPENED',
  INCIDENT_UPDATED = 'INCIDENT_UPDATED',
  INCIDENT_CLOSED = 'INCIDENT_CLOSED',
}

export enum IOTV_Thing_EventType {
  FILL_EVENT = 'FILL_EVENT',
}

export type LambdaFanOutSNSEvent = SNSEvent & {}

export type IOTV_SNS_MapLike = MapLike<string | number | boolean>

export type NetworkAWOL_SQSMessageAttrType = {
  Action: {
    DataType: 'String'
    StringValue: string
    stringValue?: string
  }
  AppId: {
    DataType: 'String'
    StringValue: string
    stringValue?: string
  }
}

export interface IOTV_SNS_PublishMessageAttributeString extends MessageAttributeValue {
  DataType: 'String'
  StringValue: string
}

// no lambda support for boolean 'true' | 'false' https://docs.aws.amazon.com/sns/latest/api/API_MessageAttributeValue.html
export interface IOTV_SNS_MessageAttribute<T extends IOTV_SNS_MapLike, k extends string> extends MessageAttributeValue {
  DataType: T[k] extends string ? 'String' : T[k] extends number ? 'Number' : T[k] extends boolean ? 'Boolean' : 'unsupported'
  StringValue: T[k] extends boolean ? string : string
}

// narrows PublishInput to keys of T
export type IOTV_SNS_PublishInput<T extends MapLike<string | boolean | number>> = Omit<PublishInput, 'MessageAttributes'> & {
  MessageAttributes?: { [k in keyof T]: k extends string ? IOTV_SNS_MessageAttribute<T, k> : undefined }
}

// no lambda support for boolean 'true' | 'false' https://docs.aws.amazon.com/sns/latest/api/API_MessageAttributeValue.html
export interface IOTV_SNS_ReceiveMessageAttribute<T extends MapLike<string | boolean | number>, k extends string> extends SNSMessageAttribute {
  Type: string //T[k] extends string ? 'String' : T[k] extends number ? 'Number' : T[k] extends boolean ? 'Boolean' : 'String',
  Value: string //T[k] extends boolean ? string : string,
}

// narrows PublishInput to keys of T
export interface IOTV_SNSMessage<T extends MapLike<string | boolean | number>> extends SNSMessage {
  MessageAttributes: { [k in keyof T]: k extends string ? IOTV_SNS_ReceiveMessageAttribute<T, k> : SNSMessageAttribute }
}

export type IOTV_SNSEventRecord<T extends MapLike<string | boolean | number>> = SNSEventRecord & {
  Sns: IOTV_SNSMessage<T>
}

export type IOTV_SNSEventRecordWithTypedPayload<MA extends MapLike<string | boolean | number>, PT> = IOTV_SNSEventRecord<MA> & { Message: PT }

export type IOTV_SNSEvent<T extends MapLike<string | boolean | number>> = SNSEvent & {
  Records: IOTV_SNSEventRecord<T>[]
}

export type SNSUnStringedMessageRequest<T1 extends MapLike<any>, T2 extends MapLike<any>> = {
  TopicArn: string
  Timestamp?: string
  Subject: string
  Message: T1
  MessageAttributes: { [k in keyof T2]: string | number | boolean }
}

/** converts a single level object to SNS Message Attributes */
export const IOTV_SNSPublishInputToMessageAttributeMap = <T extends IOTV_SNS_MapLike>(
  ob: T,
): { [k in keyof T]: k extends string ? IOTV_SNS_MessageAttribute<T, k> : undefined } => {
  const messageAttributes: MapLike<any> = {}
  Object.entries(ob).forEach(([k, v]) => {
    if (typeof v === 'string') {
      messageAttributes[k] = {
        DataType: 'String',
        StringValue: v,
      }
    } else if (typeof v === 'number') {
      messageAttributes[k] = {
        DataType: 'Number',
        StringValue: v.toString(),
      }
    } else if (typeof v === 'boolean') {
      messageAttributes[k] = {
        DataType: 'Boolean',
        StringValue: v.toString(),
      }
    } else {
      debug && console.log(`${k} with value ${v} not supported yet, only doing strings, booleans, numbers`)
    }
  })

  return messageAttributes as { [k in keyof T]: k extends string ? IOTV_SNS_MessageAttribute<T, k> : undefined }
}

export const IOTV_SNS_MOCK_OUTPUT_ObjectToMessageAttributeMap = <T extends IOTV_SNS_MapLike>(
  ob: T,
): { [k in keyof T]: k extends string ? IOTV_SNS_ReceiveMessageAttribute<T, k> : undefined } => {
  const messageAttributes: MapLike<any> = {}
  Object.entries(ob).forEach(([k, v]) => {
    if (typeof v === 'string') {
      messageAttributes[k] = {
        Type: 'String',
        Value: v,
      }
    } else if (typeof v === 'number') {
      messageAttributes[k] = {
        Type: 'Number',
        Value: v.toString(),
      }
    } else if (typeof v === 'boolean') {
      messageAttributes[k] = {
        Type: 'Boolean',
        Value: v.toString(),
      }
    } else {
      debug && console.log(`${k} with value ${v} not supported yet, only doing strings, booleans, numbers`)
    }
  })

  return messageAttributes as { [k in keyof T]: k extends string ? IOTV_SNS_ReceiveMessageAttribute<T, k> : undefined }
}

export const IOTV_SNSAttributeMapToMap = <T extends IOTV_SNS_MapLike>(messageAttributes: {
  [k in keyof T]: k extends string ? IOTV_SNS_ReceiveMessageAttribute<T, k> : SNSMessageAttribute
}): T => {
  const map: IOTV_SNS_MapLike = {}
  Object.entries(messageAttributes).forEach(([k, v]) => {
    if (v.Type === 'String') {
      map[k] = v.Value
    } else if (v.Type === 'Number') {
      map[k] = parseFloat(v.Value)
    } else if (v.Type === 'Boolean') {
      map[k] = v.Value === 'true'
    } else if (v.Value) {
      debug && console.log(`${k} with type ${typeof v} value ${v} not supported yet, only doing strings, booleans, numbers`)
    } else {
      debug && console.log(`${k} with type ${typeof v} value ${v} could not be processed`)
    }
  })

  return map as T
}

export const parseSNSRecordMesssage = <MA extends IOTV_SNS_MapLike, PT>(record: IOTV_SNSEventRecordWithTypedPayload<MA, PT>): PT | undefined => {
  let object: PT | undefined = undefined
  try {
    object = JSON.parse(record.Sns.Message)
  } catch (e: any) {
    console.log('WARN: unable to parse string', record.Sns.Message)
  }
  return object
}

export const getSNSPublishInput = <MessageT extends MapLike<any>, MessageAttsT extends IOTV_SNS_MapLike>(
  deviceControlMessageObject: SNSUnStringedMessageRequest<MessageT, MessageAttsT>,
) => {
  const publishInput: IOTV_SNS_PublishInput<MessageAttsT> = {
    ...deviceControlMessageObject,
    MessageAttributes: IOTV_SNSPublishInputToMessageAttributeMap(deviceControlMessageObject.MessageAttributes),
    Message: JSON.stringify(deviceControlMessageObject.Message),
  }
  return publishInput
}
